"use client";

import Link from "next/link";
import React from "react";
import { Link as InternalLink, Heading, Text, Select } from "ui-components";

import { scrollToDiv } from "@/src/utils/dom";

export default function TermsOfUsePage(): JSX.Element {
  return (
    <div className="my-5 lg:my-20">
      <Heading className="mb-5 lg:mb-10">Terms of Use</Heading>
      <div className="flex flex-col lg:flex-row lg:gap-x-10">
        <div className="w-1/6">
          <div className="hidden lg:flex flex-col gap-y-5">
            <Link href="#terms-of-website-use">
              <Text>1. Terms of website use</Text>
            </Link>
            <Link href="#other-applicable-terms">
              <Text>2. Other applicable terms</Text>
            </Link>
            <Link href="#definitions">
              <Text>3. Definitions</Text>
            </Link>
            <Link href="#information-about-us">
              <Text>4. Information about us</Text>
            </Link>
            <Link href="#changes-to-these-terms">
              <Text>5. Changes to these terms</Text>
            </Link>
            <Link href="#changes-to-our-site">
              <Text>6. Changes to our site</Text>
            </Link>
            <Link href="#accessing-our-site">
              <Text>7. Accessing our site</Text>
            </Link>
            <Link href="#acceptable-use-terms">
              <Text>8. Acceptable use terms</Text>
            </Link>
          </div>
          <div className="flex lg:hidden mb-5">
            <Select
              value="terms-of-website-use"
              placeholder="Terms of Use"
              options={[
                { id: "terms-of-website-use", title: "Terms of website use" },
                {
                  id: "other-applicable-terms",
                  title: "Other applicable terms",
                },
                { id: "definitions", title: "Definitions" },
                { id: "information-about-us", title: "Information about us" },
                {
                  id: "changes-to-these-terms",
                  title: "Changes to these terms",
                },
                { id: "changes-to-our-site", title: "Changes to our site" },
                { id: "accessing-our-site", title: "Accessing our site" },
                { id: "acceptable-use-terms", title: "Acceptable use terms" },
              ]}
              onSelection={(key) => {
                scrollToDiv(key.toString());
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-5 w-10/12 max-w-[759px]">
          <Heading variant="h2">
            PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE USING THIS
            SITE
          </Heading>
          <Heading id="terms-of-website-use" variant="h2">
            1. Terms of website use
          </Heading>
          <Text component="p">
            <strong>
              PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE USING THIS
              SITE
            </strong>
            <br />
            These terms tell you the rules for using our website{" "}
            <Link className="underline" href="https://mygwork.com">
              www.mygwork.com
            </Link>{" "}
            (our site). To contact us, please email{" "}
            <Link href="mailto:support@mygwork.com">support@mygwork.com</Link>.
          </Text>
          <Text component="p">
            <strong className="underline">
              By using our site you accept these terms
            </strong>
            <br />
            By using our site, you confirm that you accept these terms of use
            and that you agree to comply with them.
            <br />
            If you do not agree to these terms, you must not use our site.
            <br />
            We recommend that you print a copy of these terms for future
            reference.
          </Text>

          <Heading id="other-applicable-terms" variant="h2">
            2. Other applicable terms
          </Heading>
          <Text component="p">
            Other applicable terms and items that may apply to you.
            <br />
            These terms of use refer to the following additional terms, which
            also apply to your use of our site:
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>Our Privacy Policy.</Text>
              </li>
              <li>
                <Text>
                  Our Acceptable Use Policy, which sets out the permitted uses
                  and prohibited uses of our site. When using our site, you must
                  comply with this Acceptable Use Policy.
                </Text>
              </li>
            </ul>
          </Text>

          <Heading id="definitions" variant="h2">
            3. Definitions
          </Heading>
          <Text component="p">
            &quot;Agreement&quot; means these Terms of Use which regulate the
            relationship between myGwork and the Member;
            <br /> &quot;Communications&quot; means any communication including
            but not limited to communications by email by the Member to other
            members of myGwork;
          </Text>
          <Text component="p">
            &quot;Contribution&quot; means the contributions of the Member to
            the Website including but not limited to the text and/or images
            contained within the Members Profile; <br />
            &quot;Member&quot; means all users who have registered on myGwork
            and whose respective membership has been accepted by myGwork and
            remains valid. <br />
            &quot;Members Profile&quot; means that part of the Website
            containing information and/or pictures about a Member; <br />
            &quot;Payment Partner&quot; means Authorize.net or any third party
            on-line payment gateway service we may contract from time to time.{" "}
            <br />
            &quot;Premium Membership&quot; means the subscription-only
            membership levels which provide the Member with full access to the
            Services. This applies to a “Corporate Membership” held by a company
            or an individual who has subscribed to a “myGclub Membership”.{" "}
            <br />
          </Text>
          <Text component="p">
            &quot;Service&quot; means the service provided by myGwork to the
            Member through the Website including, but not limited to, allowing
            the Member to access and/or use the Website, the myGclub app, and
            present them access to special offers from our local partners,
            discounts to networking events, entry to exclusive events for
            myGclub Membership holders.
          </Text>

          <Heading id="information-about-us" variant="h2">
            4. Information about us
          </Heading>
          <Text component="p">
            <Link className="underline" href="https://mygwork.com">
              www.mygwork.com
            </Link>{" "}
            is a site operated by Gaubert&apos;s Brothers Limited (myGwork)
            (&quot;We&quot;). We are registered in England and Wales under
            company number 08967439 have our registered office at 86-90 Paul
            Street, London, Greater London, EC2A 4NE. VAT Number 210 3581 55.
          </Text>

          <Heading id="changes-to-these-terms" variant="h2">
            5. Changes to these terms
          </Heading>
          <Text component="p">
            We amend these terms from time to time. Every time you wish to use
            our site, please check these terms to ensure you understand the
            terms that apply at that time.
          </Text>

          <Heading id="changes-to-our-site" variant="h2">
            6. Changes to our site
          </Heading>
          <Text component="p">
            We may update and change our site from time to time to reflect
            changes to our users&apos; needs and our business priorities.
          </Text>

          <Heading id="accessing-our-site" variant="h2">
            7. Accessing our site
          </Heading>
          <Text component="p">Our site is made available free of charge.</Text>
          <Text component="p">
            We do not guarantee that our site, or any content on it, will always
            be available or be uninterrupted. We may suspend or withdraw or
            restrict the availability of all or any part of our site for
            business and operational reasons. We will try to give you reasonable
            notice of any suspension or withdrawal.
          </Text>
          <Text component="p">
            You are also responsible for ensuring that all persons who access
            our site through your internet connection are aware of these terms
            of use and other applicable terms and conditions, and that they
            comply with them.
          </Text>
          <Text component="p">
            <strong>We may transfer this agreement to someone else</strong>
            <br />
            We may transfer our rights and obligations under these terms to
            another organization. We will always tell you in writing if this
            happens and we will ensure that the transfer will not affect your
            rights under the contract.
          </Text>
          <Text component="p">
            <strong>You must keep your account details safe</strong>
            <br />
            If you choose, or you are provided with, a user identification code,
            password or any other piece of information as part of our security
            procedures, you must treat such information as confidential. You
            must not disclose it to any third party.
          </Text>
          <Text component="p">
            We have the right to disable any user identification code or
            password, whether chosen by you or allocated by us, at any time, if
            in our reasonable opinion you have failed to comply with any of the
            provisions of these terms of use.
          </Text>
          <Text component="p">
            If you know or suspect that anyone other than you know your user
            identification code or password, you must promptly notify us at{" "}
            <Link
              className="underline"
              href="mailto:support@mygwork.com"
              target="_blank"
            >
              support@mygwork.com
            </Link>
            .
          </Text>
          <Text component="p">
            <strong>How you may use material on our site</strong>
            <br />
            We are the owner or the licensee of all intellectual property rights
            in our site, and in the material published on it. Those works are
            protected by copyright laws and treaties around the world. All such
            rights are reserved.
          </Text>
          <Text component="p">
            You may print off one copy, and may download extracts, of any
            page(s) from our site for your personal use and you may draw the
            attention of others within your organisation to content posted on
            our site.
          </Text>
          <Text component="p">
            You must not modify the paper or digital copies of any materials you
            have printed off or downloaded in any way, and you must not use any
            illustrations, photographs, video or audio sequences or any graphics
            separately from any accompanying text.
          </Text>
          <Text component="p">
            Our status (and that of any identified contributors) as the authors
            of content on our site must always be acknowledged. You must not use
            any part of the content on our site for commercial purposes without
            obtaining a licence to do so from us or our licensors.
          </Text>
          <Text component="p">
            If you print off, copy or download any part of our site in breach of
            these terms of use, your right to use our site will cease
            immediately and you must, at our option, return or destroy any
            copies of the materials you have made.
          </Text>
          <Text component="p">
            <strong>Do not rely on information on this site</strong>
            <br />
            The content on our site is provided for general information only. It
            is not intended to amount to advice on which you should rely. You
            must obtain professional or specialist advice before taking, or
            refraining from, any action on the basis of the content on our site.
          </Text>
          <Text component="p">
            Although we make reasonable efforts to update the information on our
            site, we make no representations, warranties or guarantees, whether
            express or implied, that the content on our site is accurate,
            complete or up to date.
          </Text>
          <Text component="p">
            <strong>We are not responsible for websites we link to</strong>
            <br />
            Where our site contains links to other sites and resources provided
            by third parties, these links are provided for your information
            only. Such links should not be interpreted as approval by us of
            those linked websites or information you may obtain from them.
            <br />
            We have no control over the contents of those sites or resources.
          </Text>
          <Text component="p">
            <strong>User-generated content is not approved by us</strong>
            <br />
            This website may include information and materials uploaded by other
            users of the site, including to bulletin boards and chat rooms. This
            information and these materials have not been verified or approved
            by us. The views expressed by other users on our site do not
            represent our views or values.
          </Text>
          <Text component="p">
            <strong>
              How to complain about content uploaded by other users
            </strong>
            <br />
            If you wish to complain about content uploaded by other users,
            please contact us on{" "}
            <Link href="mailto:support@mygwork.com">support@mygwork.com</Link>
          </Text>
          <Text component="p">
            <strong>
              Our responsibility for loss or damage suffered by you
            </strong>
            <br />
            <strong>Whether you are a consumer or a business user:</strong>
            <br />
            We do not exclude or limit in any way our liability to you where it
            would be unlawful to do so. This includes liability for death or
            personal injury caused by our negligence or the negligence of our
            employees, agents or subcontractors and for fraud or fraudulent
            misrepresentation.
          </Text>
          <Text component="p">
            <strong>If you are a business user:</strong>
            <br />
            We exclude all implied conditions, warranties, representations or
            other terms that may apply to our site or any content on it.
          </Text>
          <Text component="div">
            We will not be liable to you for any loss or damage, whether in
            contract, tort (including negligence), breach of statutory duty, or
            otherwise, even if foreseeable, arising under or in connection with:{" "}
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>use of, or inability to use, our site; or</Text>
              </li>
              <li>
                <Text>
                  use of or reliance on any content displayed on our site.
                </Text>
              </li>
              <li>
                <Text>In particular, we will not be liable for:</Text>
              </li>
              <li>
                <Text>loss of profits, sales, business, or revenue;</Text>
              </li>
              <li>
                <Text>business interruption;</Text>
              </li>
              <li>
                <Text>loss of anticipated savings;</Text>
              </li>
              <li>
                <Text>
                  loss of business opportunity, goodwill or reputation; or
                </Text>
              </li>
              <li>
                <Text>any indirect or consequential loss or damage.</Text>
              </li>
            </ul>
          </Text>
          <Text component="p">
            <strong>If you are a consumer user:</strong>
            <br />
            Please note that we only provide our site for domestic and private
            use. You agree not to use our site for any commercial or business
            purposes, and we have no liability to you for any loss of profit,
            loss of business, business interruption, or loss of business
            opportunity.
          </Text>
          <Text component="p">
            <strong>How we may use your personal information</strong>
            <br />
            We will only use your personal information as set out in our{" "}
            <InternalLink href="/privacy-policy" className="underline">
              PRIVACY POLICY
            </InternalLink>
            .
          </Text>
          <Text component="p">
            <strong>Uploading content to our site</strong>
            <br />
            Whenever you make use of a feature that allows you to upload content
            to our site, or to make contact with other users of our site, you
            must comply with the content standards set out in our Acceptable Use
            Policy.
            <br />
            You warrant that any such contribution does comply with those
            standards, and you will be liable to us and indemnify us for any
            breach of that warranty. This means you will be responsible for any
            loss or damage we suffer as a result of your breach of warranty.
          </Text>
          <Text component="p">
            Any content you upload to our site will be considered
            non-confidential and non-proprietary. You retain all of your
            ownership rights in your content, but you are required to grant us
            and other users of our site a limited licence to use, store and copy
            that content and to distribute and make it available to third
            parties. The rights you license to us are described in Rights you
            are giving us to use material you upload below.
          </Text>
          <Text component="p">
            We also have the right to disclose your identity to any third party
            who is claiming that any content posted or uploaded by you to our
            site constitutes a violation of their intellectual property rights,
            or of their right to privacy.
          </Text>
          <Text component="p">
            We have the right to remove any posting you make on our site if, in
            our opinion, your post does not comply with the content standards
            set out in our Acceptable Use Policy.
            <br />
            You are solely responsible for securing and backing up your content.
            <br />
            We do not store terrorist content.
          </Text>
          <Text component="p">
            <strong>Rights you are giving us to use material you upload</strong>
            <br />
            When you upload or post content to our site, you grant us the
            following rights to use that content:
            <br />
            LICENCES GRANTED TO THE WEBSITE OWNER.
            <br />
            LICENCES GRANTED TO OTHER USERS OF THE SITE OR TO THIRD PARTIES.
            <br />
          </Text>
          <Text component="p">
            <strong>
              We are not responsible for viruses and you must not introduce them
            </strong>
          </Text>
          <Text component="p">
            We do not guarantee that our site will be secure or free from bugs
            or viruses.You are responsible for configuring your information
            technology, computer programmes and platform to access our site. You
            should use your own virus protection software.
          </Text>
          <Text component="p">
            You must not misuse our site by knowingly introducing viruses,
            trojans, worms, logic bombs or other material that is malicious or
            technologically harmful. You must not attempt to gain unauthorised
            access to our site, the server on which our site is stored, or any
            server, computer or database connected to our site. You must not
            attack our site via a denial-of-service attack or a distributed
            denial-of service attack. By breaching this provision, you would
            commit a criminal offence under the Computer Misuse Act 1990. We
            will report any such breach to the relevant law enforcement
            authorities, and we will co-operate with those authorities by
            disclosing your identity to them. In the event of such a breach,
            your right to use our site will cease immediately.
          </Text>
          <Text component="p">
            <strong>Rules about linking to our site</strong>
            <br />
            You may link to our home page, provided you do so in a way that is
            fair and legal and does not damage our reputation or take advantage
            of it.You must not establish a link in such a way as to suggest any
            form of association, approval or endorsement on our part where none
            exists.You must not establish a link to our site in any website that
            is not owned by you.
            <br />
            Our site must not be framed on any other site, nor may you create a
            link to any part of our site other than the home page.
            <br />
            We reserve the right to withdraw linking permission without notice.
            <br />
            The website in which you are linking must comply in all respects
            with the content standards set out in our Acceptable Use Policy.
            <br />
            If you wish to link to or make any use of content on our site other
            than that set out above, please contact{" "}
            <Link href="mailto:support@mygwork.com">support@mygwork.com</Link>
          </Text>
          <Text component="p">
            <strong>Which country&apos;s laws apply to any disputes?</strong>
            <br />
            If you are a consumer, please note that these terms of use, their
            subject matter and their formation, are governed by English law. You
            and we both agree that the courts of England and Wales will have
            exclusive jurisdiction except that if you are a resident of Northern
            Ireland you may also bring proceedings in Northern Ireland, and if
            you are resident of Scotland, you may also bring proceedings in
            Scotland.
          </Text>
          <Text component="p">
            If you are a business, these terms of use, their subject matter and
            their formation (and any non-contractual disputes or claims) are
            governed by English law. We both agree to the exclusive jurisdiction
            of the courts of England and Wales.
          </Text>
          <Text component="p">
            <strong>Our trade marks are registered</strong>
            <br />
          </Text>
          <Text component="p">
            myGwork and WorkPride by myGwork are UK registered trade marks of
            Gaubert’s Borthers Limited. You are not permitted to use them
            without our approval, unless they are part of material you are using
            as permitted under How you may use material on our site.
          </Text>

          <Heading id="acceptable-use-terms" variant="h2">
            8. Acceptable use of terms
          </Heading>
          <Text component="p">
            <strong>What&apos;s in these terms?</strong>
            <br />
            This acceptable use policy sets out the content standards that apply
            when you upload content to our site, make contact with other users
            on our site, link to our site, or interact with our site in any
            other way,
          </Text>
          <Text component="p">
            <strong>Who we are and how to contact us</strong>
            <br />
            <Link className="underline" href="https://mygwork.com">
              www.mygwork.com
            </Link>{" "}
            is a site operated by Gaubert’s Brothers Limited (myGwork)
            (&quot;We&quot;). We are registered in England and Wales under
            company number 08967439 and have our registered office at 86-90 Paul
            Street, London, Greater London, EC2A 4NE. VAT Number 210 3581 55.
            <br />
            To contact us, please email{" "}
            <Link href="mailto:support@mygwork.com">support@mygwork.com</Link>
          </Text>
          <Text component="p">
            <strong>By using our site you accept these terms</strong>
            <br />
            By using our site, you confirm that you accept the terms of this
            policy and that you agree to comply with them.
            <br />
            We recommend that you print a copy of these terms for future
            reference.
            <br />
            There are other terms that may apply to you
            <br />
            Our Terms of website use also apply to your use of our site.
            <br />
          </Text>
          <Text component="p">
            <strong>We may make changes to the terms of this policy</strong>
            <br />
            We amend these terms from time to time. Every time you wish to use
            our site, please check these terms to ensure you understand the
            terms that apply at that time.
          </Text>
          <Text component="div">
            <strong>Prohibited uses</strong>
            <br />
            You may use our site only for lawful purposes.
            <br />
            You may not use our site:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>
                  In any way that breaches any applicable local, national or
                  international law or regulation.
                </Text>
              </li>
              <li>
                <Text>
                  In any way that is unlawful or fraudulent or has any unlawful
                  or fraudulent purpose or effect.
                </Text>
              </li>
              <li>
                <Text>
                  For the purpose of harming or attempting to harm minors in any
                  way.
                </Text>
              </li>
              <li>
                <Text>
                  To bully, insult, intimidate or humiliate any person.
                </Text>
              </li>
              <li>
                <Text>
                  To send, knowingly receive, upload, download, use or re-use
                  any material which does not comply with our content standards.
                </Text>
              </li>
              <li>
                <Text>
                  To transmit, or procure the sending of, any unsolicited or
                  unauthorised advertising or promotional material or any other
                  form of similar solicitation (spam).
                </Text>
              </li>
              <li>
                <Text>
                  To knowingly transmit any data, send or upload any material
                  that contains viruses, Trojan horses, worms, time-bombs,
                  keystroke loggers, spyware, adware or any other harmful
                  programs or similar computer code designed to adversely affect
                  the operation of any computer software or hardware.
                </Text>
              </li>
              <li>
                <Text>
                  To include material on your personal profile page which
                  contains video, audio, photographs, or images of any person
                  under the age of eighteen (18) at all or any person over the
                  age of eighteen (18) without his or her express permission.
                </Text>
              </li>
            </ul>
          </Text>
          <Text component="div">
            <strong>You also agree:</strong>
            <br />
            Not to reproduce, duplicate, copy or re-sell any part of our site in
            contravention of the provisions of our terms of website use.
            <br />
            Not to access without authority, interfere with, damage or disrupt:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>any part of our site;</Text>
              </li>
              <li>
                <Text>
                  any equipment or network on which our site is stored;
                </Text>
              </li>
              <li>
                <Text>
                  For the purpose of harming or attempting to harm minors in any
                  way.
                </Text>
              </li>
              <li>
                <Text>any software used in the provision of our site; or</Text>
              </li>
              <li>
                <Text>
                  any equipment or network or software owned or used by any
                  third party.
                </Text>
              </li>
            </ul>
          </Text>
          <Text component="div">
            <strong>Interactive services</strong>
            <br />
            We may from time to time provide interactive services on our site,
            including, without limitation:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>Chat rooms.</Text>
              </li>
              <li>
                <Text>Bulletin boards.</Text>
              </li>
              <li>
                <Text>Webinars.</Text>
              </li>
            </ul>
            Where we do provide any interactive service, we will provide clear
            information to you about the kind of service offered, if it is
            moderated and what form of moderation is used (including whether it
            is human or technical).
          </Text>
          <Text component="p">
            We will do our best to assess any possible risks for users (and in
            particular, for children) from third parties when they use any
            interactive service provided on our site, and we will decide in each
            case whether it is appropriate to use moderation of the relevant
            service (including what kind of moderation to use) in the light of
            those risks. However, we are under no obligation to oversee, monitor
            or moderate any interactive service we provide on our site, and we
            expressly exclude our liability for any loss or damage arising from
            the use of any interactive service by a user in contravention of our
            content standards, whether the service is moderated or not.
          </Text>
          <Text component="p">
            The use of any of our interactive services by a minor is subject to
            the consent of their parent or guardian. We advise parents who
            permit their children to use an interactive service that it is
            important that they communicate with their children about their
            safety online, as moderation is not fool proof. Minors who are using
            any interactive service should be made aware of the potential risks
            to them.
          </Text>
          <Text component="p">
            Where we do moderate an interactive service, we will normally
            provide you with a means of contacting the moderator, should a
            concern or difficulty arise.
          </Text>
          <Text component="div">
            <strong>Content standards</strong>
            <br />
            These content standards apply to any and all material which you
            contribute to our site (Contribution), and to any interactive
            services associated with it.
            <br />
            The Content Standards must be complied with in spirit as well as to
            the letter. The standards apply to each part of any Contribution as
            well as to its whole.
            <br />
            myGwork will determine, in its discretion, whether a Contribution
            breaches the Content Standards.
            <br />
            A Contribution must:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>Be accurate (where it states facts).</Text>
              </li>
              <li>
                <Text>Be genuinely held (where it states opinions).</Text>
              </li>
              <li>
                <Text>
                  Comply with the law applicable in England and Wales and in any
                  country from which it is posted.
                </Text>
              </li>
            </ul>
          </Text>
          <Text component="div">
            <strong>A Contribution must not:</strong>
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>Be defamatory of any person.</Text>
              </li>
              <li>
                <Text>Be obscene, offensive, hateful or inflammatory.</Text>
              </li>
              <li>
                <Text>Bully, insult, intimidate or humiliate.</Text>
              </li>
              <li>
                <Text>Promote sexually explicit material.</Text>
              </li>
              <li>
                <Text>Include child sexual abuse material.</Text>
              </li>
              <li>
                <Text>Promote violence.</Text>
              </li>
              <li>
                <Text>
                  Promote discrimination based on race, sex, religion,
                  nationality, disability, sexual orientation or age.
                </Text>
              </li>
              <li>
                <Text>
                  Infringe any copyright, database right or trademark of any
                  other person.
                </Text>
              </li>
              <li>
                <Text>Be likely to deceive any person.</Text>
              </li>
              <li>
                <Text>
                  Breach any legal duty owed to a third party, such as a
                  contractual duty or a duty of confidence.
                </Text>
              </li>
              <li>
                <Text>Promote any illegal activity.</Text>
              </li>
              <li>
                <Text>Be in contempt of court.</Text>
              </li>
              <li>
                <Text>
                  Be threatening, abuse or invade another&apos;s privacy, or
                  cause annoyance, inconvenience or needless anxiety.
                </Text>
              </li>
              <li>
                <Text>
                  Be likely to harass, upset, embarrass, alarm or annoy any
                  other person.
                </Text>
              </li>
              <li>
                <Text>
                  Impersonate any person or misrepresent your identity or
                  affiliation with any person.
                </Text>
              </li>
              <li>
                <Text>
                  Give the impression that the Contribution emanates from
                  myGwork or Gaubert’s Brothers Limited if this is not the case.
                </Text>
              </li>
              <li>
                <Text>
                  Advocate, promote, incite any party to commit, or assist any
                  unlawful or criminal act such as (by way of example only)
                  copyright infringement or computer misuse.
                </Text>
              </li>
              <li>
                <Text>
                  Contain a statement which you know or believe, or have
                  reasonable grounds for believing, that members of the public
                  to whom the statement is, or is to be, published are likely to
                  understand as a direct or indirect encouragement or other
                  inducement to the commission, preparation or instigation of
                  acts of terrorism.
                </Text>
              </li>
              <li>
                <Text>
                  Contain any advertising or promote any services or web links
                  to other sites.
                </Text>
              </li>
            </ul>
          </Text>
          <Text component="div">
            We provide you with the opportunity to add a photograph to your
            profile to help others recognize you. Your photo can be removed by
            myGwork if your profile image doesn&apos;t include your likeness or
            a headshot photo. Some examples of photos that shouldn&apos;t be
            used are:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>Company Logos</Text>
              </li>
              <li>
                <Text>Landscapes</Text>
              </li>
              <li>
                <Text>AnimalsWords or phrases</Text>
              </li>
              <li>
                <Text>Topless photos</Text>
              </li>
            </ul>
            All photos must comply with this acceptable use policy. If we remove
            your photo, you can upload a different photo of yourself to remedy
            this situation.
          </Text>
          <Text component="div">
            <strong>Breach of this policy</strong>
            <br />
            When we consider that a breach of this acceptable use policy has
            occurred, we may take such action as we deem appropriate.
            <br />
            Failure to comply with this acceptable use policy constitutes a
            material breach of the terms of use upon which you are permitted to
            use our site, and may result in our taking all or any of the
            following actions:
            <br />
            <ul className="list-disc text-sm font-medium list-inside">
              <li>
                <Text>
                  Immediate, temporary or permanent withdrawal of your right to
                  use our site.
                </Text>
              </li>
              <li>
                <Text>
                  Immediate, temporary or permanent removal of any Contribution
                  uploaded by you to our site.
                </Text>
              </li>
              <li>
                <Text>Issue of a warning to you.</Text>
              </li>
              <li>
                <Text>
                  Legal proceedings against you for reimbursement of all costs
                  on an indemnity basis (including, but not limited to,
                  reasonable administrative and legal costs) resulting from the
                  breach.
                </Text>
              </li>
              <li>
                <Text>Further legal action against you.</Text>
              </li>
              <li>
                <Text>
                  Disclosure of such information to law enforcement authorities
                  as we reasonably feel is necessary or as required by law.
                </Text>
              </li>
            </ul>
            <br />
            We exclude our liability for all action we may take in response to
            breaches of this acceptable use policy. The actions we may take are
            not limited to those described above, and we may take any other
            action we reasonably deem appropriate.
          </Text>
          <Text component="p">
            <strong>Which country&apos;s laws apply to any disputes?</strong>
            <br />
            If you are a consumer, please note that the terms of this policy,
            its subject matter and its formation are governed by English law.
            You and we both agree that the courts of England and Wales will have
            exclusive jurisdiction except that if you are a resident of Northern
            Ireland you may also bring proceedings in Northern Ireland, and if
            you are resident of Scotland, you may also bring proceedings in
            Scotland.
          </Text>
          <Text component="p">
            If you are a business, the terms of this policy, its subject matter
            and its formation (and any non-contractual disputes or claims) are
            governed by English law. We both agree to the exclusive jurisdiction
            of the courts of England and Wales.
          </Text>
        </div>
      </div>
    </div>
  );
}
